import { AccountType, Product, UserRole } from '@logz-pkg/enums';
import {
  accountManagementCollectionApiService,
  logShippingTokenApiService,
  metricsApiBudgetService,
  sessionStateService,
  tracingAccountsApiService,
} from '@logz-pkg/frontend-services';
import { AccountInfoModel } from '@logz-pkg/models';
import { Observable } from '@logz-pkg/observable';
import { metricsService } from 'services/metrics/metrics.service';
import { IContentTab } from 'ui/components/Integrations/Markdown/extract-tabs';

class IntegrationsHubStateService {
  readonly logsAccountsDetails = new Observable<AccountInfoModel[] | null>(null);
  readonly metricsAccountsDetails = new Observable<AccountInfoModel[] | null>(null);
  readonly tracingAccountsDetails = new Observable<AccountInfoModel[] | null>(null);
  readonly selectedLogAnalyticsAccountDetails = new Observable<AccountInfoModel | null>(null);
  readonly selectedMetricAccountDetails = new Observable<AccountInfoModel | null>(null);
  readonly selectedTracingAccountDetails = new Observable<AccountInfoModel | null>(null);
  readonly isFetchingAccountsData = new Observable<boolean>(false);
  readonly selectedGlobalTab = new Observable<IContentTab | null>(null);

  fetchAndSetAccountsDetails = async (): Promise<void> => {
    const session = sessionStateService.session.get();
    const isReadOnlyView = session?.loggedInUser?.role === UserRole.ReadOnly;

    if (!session?.loggedInUser || isReadOnlyView) return;

    this.isFetchingAccountsData.set(true);

    const [logsAccountsDetails, metricsAccountsNamesAndTokens, tracingAccountsNamesAndTokens] = await Promise.all([
      this.getLogsAccountsNamesAndTokens(),
      this.getMetricsAccountsNamesAndTokens(),
      this.getTracingAccountsNamesAndTokens(),
    ]);

    this.logsAccountsDetails.set(logsAccountsDetails);
    this.metricsAccountsDetails.set(metricsAccountsNamesAndTokens ?? null);
    this.tracingAccountsDetails.set(tracingAccountsNamesAndTokens ?? null);

    this.isFetchingAccountsData.set(false);
  };

  clear = () => {
    this.logsAccountsDetails.set(null);
    this.metricsAccountsDetails.set(null);
    this.tracingAccountsDetails.set(null);
    this.selectedLogAnalyticsAccountDetails.set(null);
    this.selectedMetricAccountDetails.set(null);
    this.selectedTracingAccountDetails.set(null);
    this.isFetchingAccountsData.set(false);
  };

  setSelectedLogAnalyticAccounts = this.selectedLogAnalyticsAccountDetails.set;
  setSelectedGlobalTab = this.selectedGlobalTab.set;

  setSelectedMetricAccounts = this.selectedMetricAccountDetails.set;

  setSelectedTracingAccounts = this.selectedTracingAccountDetails.set;

  private getLogsAccountsNamesAndTokens = async (): Promise<AccountInfoModel[]> => {
    let logsAccountsDetails;
    const session = sessionStateService.session.get();

    if (session?.loggedInAccount?.type !== AccountType.Owner) {
      const { token } = (await logShippingTokenApiService.getDefaultToken()) ?? {};

      logsAccountsDetails = [
        {
          name: session?.loggedInAccount.name,
          token,
        },
      ];
    } else {
      const accounts = (await accountManagementCollectionApiService.getAll()) ?? [];

      logsAccountsDetails = accounts.map(({ account: { accountName: name, accountToken: token } }) => ({
        name,
        token,
      }));
    }

    return logsAccountsDetails;
  };

  private getMetricsAccountsNamesAndTokens = async (): Promise<AccountInfoModel[] | undefined> => {
    const hasMetrics = (await metricsService.getState()).hasMetricsAccount;

    if (!hasMetrics) return undefined;

    const session = sessionStateService.session.get();
    const isOwnerAccount = session?.loggedInAccount?.type === AccountType.Owner;

    const metricsAccountsFetcher = isOwnerAccount
      ? () => metricsApiBudgetService.getDetailed().then(data => data?.metricsAccounts)
      : metricsService.getAuthorizedMetricsAccounts;

    const metricsAccounts = await metricsAccountsFetcher();

    if (metricsAccounts) {
      const metricsAccountsNamesAndTokens = metricsAccounts.map(({ name, token }) => ({ name, token }));

      return metricsAccountsNamesAndTokens;
    }
  };

  getAndSetMetricsAccountsNamesAndTokens = async (): Promise<void> => {
    const metricsAccountsNamesAndTokens = await this.getMetricsAccountsNamesAndTokens();

    this.metricsAccountsDetails.set(metricsAccountsNamesAndTokens ?? null);
  };

  private getTracingAccountsNamesAndTokens = async (): Promise<AccountInfoModel[] | undefined> => {
    const tracingAccounts = await tracingAccountsApiService.getAllTracingAccounts();

    if (!tracingAccounts?.length) return undefined;

    const tracingSpmAccounts = await Promise.all(
      tracingAccounts.map(t =>
        tracingAccountsApiService.getTracingMetricsAccount(t.accountId).then(d => {
          return {
            tracingAccountId: t.accountId,
            ...d,
          };
        }),
      ),
    );

    const tracingAccountsNamesAndTokens: AccountInfoModel[] = tracingAccounts.map(
      ({ accountName: name, token, accountId }) => {
        const tracingMetricsAccounts = tracingSpmAccounts
          .filter(t => t.tracingAccountId === accountId)
          .map(t => ({ ...t, type: 'SPM' }));

        return {
          name,
          token,
          connectedAccounts: tracingMetricsAccounts.length === 0 ? undefined : tracingMetricsAccounts[0],
        } as AccountInfoModel;
      },
    );

    return tracingAccountsNamesAndTokens;
  };

  getAndSetTracingAccountsNamesAndTokens = async (): Promise<void> => {
    const tracingAccountsNamesAndTokens = await this.getTracingAccountsNamesAndTokens();

    this.tracingAccountsDetails.set(tracingAccountsNamesAndTokens ?? null);
  };

  setSelectedAccount = (accountType: Product, data: AccountInfoModel) => {
    if (accountType === Product.LogAnalytics) {
      this.setSelectedLogAnalyticAccounts(data);
    }

    if (accountType === Product.Metrics) {
      this.setSelectedMetricAccounts(data);
    }

    if (accountType === Product.Tracing) {
      this.setSelectedTracingAccounts(data);
    }
  };
}

export const integrationsHubStateService = new IntegrationsHubStateService();
